/**
 * subscription.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Client-side subscription service for Margin Pro.
 *
 * Reads the user's plan and subscriptionStatus from their Firestore user doc
 * (set server-side by Cloud Functions — never client-writable).
 *
 * Plan values stored in Firestore:
 *   'Free'       → freemium, 20-note cap, no attachments, no Global Notes
 *   'Personal'   → $5.99/mo or $49/yr, unlimited + attachments + Global Notes
 *   'enterprise' → team workspaces + admin governance
 */

import { db } from '../config';
import { doc, getDoc } from 'firebase/firestore';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Plan = 'Free' | 'Personal' | 'Professional' | 'enterprise';

/** The live Microsoft Commercial Marketplace SaaS subscription offer. Single source of
 *  truth for every "Subscribe / Upgrade / Manage on Microsoft" link across the app. */
export const MARKETPLACE_OFFER_URL =
  'https://marketplace.microsoft.com/en-us/product/saas/marginpro1779153233790.margin-pro-saas?tab=Overview';

export interface PlanLimits {
  maxNotes: number;       // Infinity = unlimited
  attachments: boolean;   // Can upload images / PDFs
  globalNotes: boolean;   // Can mark notes as Global (cross-document)
  teamWorkspaces: boolean;// Can share notes with a team/tenant
}

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  Free: {
    maxNotes:       0,      // Locked on unsubscribed/expired tier (forces trial/subscription)
    attachments:    false,
    globalNotes:    false,
    teamWorkspaces: false,
  },
  Personal: {
    maxNotes:       Infinity,
    attachments:    true,
    globalNotes:    true,
    teamWorkspaces: false,
  },
  Professional: {
    maxNotes:       Infinity,
    attachments:    true,
    globalNotes:    true,
    teamWorkspaces: false,
  },
  enterprise: {
    maxNotes:       Infinity,
    attachments:    true,
    globalNotes:    true,
    teamWorkspaces: true,
  },
};

// ─── Owner / developer override ──────────────────────────────────────────────
// These accounts always receive Professional access regardless of Firestore plan.
// Remove or empty this set before public launch if billing is fully configured.
export const OWNER_EMAILS = new Set([
  'johncyork@gmail.com',
  'john@marginproapp.com',
]);

// Owner override is a DEV/STAGING convenience only — it must NEVER grant comped
// Professional access in production. process.env.VITE_ENVIRONMENT is inlined at
// build time by webpack DefinePlugin (build:prod => 'production').
export const OWNER_OVERRIDE_ENABLED =
  ((process.env.VITE_ENVIRONMENT as string) || 'production') !== 'production';

// ─── Cache ───────────────────────────────────────────────────────────────────
// Avoid hitting Firestore on every note save. Cache is invalidated on sign-out.

let _cachedPlan: Plan | null = null;
let _cacheUid: string | null = null;

export function clearPlanCache(): void {
  _cachedPlan = null;
  _cacheUid = null;
}

// ─── Main function ───────────────────────────────────────────────────────────

/**
 * Returns the effective Plan for a user.
 * Falls back to 'Free' if the document doesn't exist or is unreadable.
 * Suspended subscriptions are treated as Free until reinstated.
 */
export async function getUserPlan(uid: string): Promise<Plan> {
  // Return cached value for same uid
  if (_cacheUid === uid && _cachedPlan !== null) {
    return _cachedPlan;
  }

  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return promoteFree(uid);

    const data = snap.data();

    // Owner override — always Professional regardless of billing state
    const email = (data?.email as string | undefined)?.toLowerCase();
    if (OWNER_OVERRIDE_ENABLED && email && OWNER_EMAILS.has(email)) {
      return cachePlan(uid, 'Professional');
    }

    const rawPlan   = data?.plan as string | undefined;
    const status    = data?.subscriptionStatus as string | undefined;

    // Suspended → treat as Free until Microsoft reinstates (checked before the trial
    // so a suspended subscriber can't be re-granted access by the trial window).
    if (status === 'suspended' || status === 'cancelled') {
      return promoteFree(uid);
    }

    // Universal free trial — every account gets TRIAL_DAYS of Professional from the
    // moment its profile was created. Derived from the immutable `createdAt` timestamp
    // rather than a stamped field, so it:
    //   • needs no Cloud Function / email at sign-in (the email-population race that
    //     previously left reviewer accounts un-stamped no longer matters), and
    //   • covers reviewer accounts of ANY type — @microsoft.com, *.onmicrosoft.com,
    //     blank-email, or third-party validation vendors (e.g. *.helios-studio.at).
    // After the window, we fall through to the real plan below (NOT a hard Free), so a
    // genuine paid subscriber whose trial elapsed keeps their purchased plan.
    const TRIAL_DAYS = 30;
    const createdAtRaw: any = data?.createdAt;
    if (createdAtRaw) {
      const createdDate: Date = createdAtRaw.toDate
        ? createdAtRaw.toDate()
        : new Date(createdAtRaw);
      if (!isNaN(createdDate.getTime())) {
        const trialEnd = new Date(createdDate.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
        if (new Date() < trialEnd) {
          return cachePlan(uid, 'Professional');
        }
      }
    }

    if (rawPlan === 'Personal' || rawPlan === 'personal-monthly' || rawPlan === 'personal-annual') {
      return cachePlan(uid, 'Personal');
    }
    if (rawPlan === 'Professional' || rawPlan === 'professional-monthly' || rawPlan === 'professional-annual' || rawPlan === 'Personal Pro') {
      return cachePlan(uid, 'Professional');
    }
    if (rawPlan === 'enterprise' || rawPlan === 'Enterprise') {
      return cachePlan(uid, 'enterprise');
    }
    return promoteFree(uid);
  } catch (err) {
    console.warn('[subscription] Failed to read user plan, defaulting to Free:', err);
    return 'Free';
  }
}

function promoteFree(uid: string): Plan {
  return cachePlan(uid, 'Free');
}

function cachePlan(uid: string, plan: Plan): Plan {
  _cacheUid = uid;
  _cachedPlan = plan;
  return plan;
}

// ─── Trial status ──────────────────────────────────────────────────────────────

export interface TrialInfo {
  /** Currently inside the free-trial window AND not on a paid subscription. */
  onTrial: boolean;
  /** Has an active paid subscription (Stripe or marketplace). */
  isPaid: boolean;
  /** When the trial ends (createdAt + 30 days), or null if unknown. */
  trialEnd: Date | null;
  /** Whole days remaining in the trial (0 once expired). */
  daysLeft: number;
}

const TRIAL_DAYS = 30;
const PAID_PLAN_VALUES = [
  'Personal', 'personal-monthly', 'personal-annual', 'Personal Pro',
  'Professional', 'professional-monthly', 'professional-annual',
  'enterprise', 'Enterprise',
];

/**
 * Returns trial/paid status for the badge + "subscribe during trial" UI. A trial user
 * resolves to Professional via createdAt (see getUserPlan) but has no paid plan written,
 * so onTrial distinguishes "Professional (trial)" from "Professional (paid)".
 */
export async function getTrialInfo(uid: string): Promise<TrialInfo> {
  const empty: TrialInfo = { onTrial: false, isPaid: false, trialEnd: null, daysLeft: 0 };
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return empty;
    const data = snap.data();

    const rawPlan = data?.plan as string | undefined;
    const status = data?.subscriptionStatus as string | undefined;
    const isPaid = !!rawPlan && PAID_PLAN_VALUES.includes(rawPlan)
      && status !== 'suspended' && status !== 'cancelled';

    let trialEnd: Date | null = null;
    const createdAtRaw: any = data?.createdAt;
    if (createdAtRaw) {
      const createdDate: Date = createdAtRaw.toDate ? createdAtRaw.toDate() : new Date(createdAtRaw);
      if (!isNaN(createdDate.getTime())) {
        trialEnd = new Date(createdDate.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
      }
    }
    const now = Date.now();
    const trialActive = !!trialEnd && now < trialEnd.getTime();
    const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd.getTime() - now) / 86_400_000)) : 0;

    return { onTrial: trialActive && !isPaid, isPaid, trialEnd, daysLeft };
  } catch {
    return empty;
  }
}

// ─── Feature gate helpers ─────────────────────────────────────────────────────

/** Returns the feature limits for a given plan. */
export function getLimitsForPlan(plan: Plan): PlanLimits {
  return PLAN_LIMITS[plan];
}

export interface GateCheckResult {
  allowed: boolean;
  /** Human-readable reason shown in the upgrade prompt, if blocked */
  upgradeMessage?: string;
}

/**
 * Check whether the user can save a new note given their current plan and note count.
 */
export function checkNoteCap(plan: Plan, currentNoteCount: number): GateCheckResult {
  const limits = PLAN_LIMITS[plan];
  if (currentNoteCount >= limits.maxNotes) {
    return {
      allowed: false,
      upgradeMessage: `You've reached the ${limits.maxNotes}-note limit on the Free plan. Upgrade to Personal for unlimited notes.`,
    };
  }
  return { allowed: true };
}

/**
 * Check whether the user can add an attachment.
 */
export function checkAttachment(plan: Plan): GateCheckResult {
  if (!PLAN_LIMITS[plan].attachments) {
    return {
      allowed: false,
      upgradeMessage: 'File attachments are a Personal plan feature. Upgrade to attach images and PDFs to your notes.',
    };
  }
  return { allowed: true };
}

/**
 * Check whether the user can mark a note as Global.
 */
export function checkGlobalNote(plan: Plan): GateCheckResult {
  if (!PLAN_LIMITS[plan].globalNotes) {
    return {
      allowed: false,
      upgradeMessage: 'Global Notes (cross-document) are a Personal plan feature. Upgrade to share notes across all your documents.',
    };
  }
  return { allowed: true };
}
